import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { navLinks, allAnchors } from '@/mocks/home';
import SmartLink from '@/components/feature/SmartLink';

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isActive = (href: string) => href.startsWith('/') && pathname.startsWith(href);

  // Black over the first screen (home: until the pinned hero ends), deep navy after.
  const [past, setPast] = useState(false);
  useEffect(() => {
    let raf = 0;
    const check = () => {
      raf = 0;
      const hero = document.getElementById('top');
      const box = hero?.parentElement?.classList.contains('pin-spacer') ? hero.parentElement : hero;
      setPast(box ? box.getBoundingClientRect().bottom <= 56 : window.scrollY > window.innerHeight - 56);
    };
    const onScroll = () => { if (!raf) raf = window.requestAnimationFrame(check); };
    check();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    if (open) window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <nav className={`h-14 ${past ? 'bg-primary-500' : 'bg-secondary-950'} transition-colors duration-500 motion-reduce:transition-none text-background-50 flex items-center justify-between px-4 md:px-10`}>
        <SmartLink href="#top" className="font-heading text-lg tracking-[0.35em] font-light cursor-pointer whitespace-nowrap">AUREN</SmartLink>
        <div className="flex items-center gap-8">
          <ul className="hidden md:flex items-center gap-8">
            {navLinks.map((l) => (
              <li key={l.href}>
                <SmartLink href={l.href} ariaLabel={isActive(l.href) ? `${l.label} (current page)` : undefined} className={`label-xs hover:text-background-50 link-line pb-1 cursor-pointer whitespace-nowrap ${isActive(l.href) ? 'text-background-50 !bg-[length:100%_1px]' : 'text-background-50/80'}`}>{l.label}</SmartLink>
              </li>
            ))}
          </ul>
          <button type="button" aria-label="Open menu" onClick={() => setOpen(true)} className="w-8 h-8 flex items-center justify-center cursor-pointer whitespace-nowrap">
            <i className="ri-menu-4-line text-xl" />
          </button>
        </div>
      </nav>

      <div className={`fixed inset-0 bg-secondary-950 text-background-50 transition-opacity duration-500 ${open ? 'opacity-100 pointer-events-auto visible' : 'opacity-0 pointer-events-none invisible'}`} aria-hidden={!open} role="dialog" aria-label="Site menu">
        <div className="h-14 flex items-center justify-between px-4 md:px-10 border-b border-background-50/10">
          <span className="font-heading text-lg tracking-[0.35em] font-light">AUREN</span>
          <button type="button" aria-label="Close menu" onClick={() => setOpen(false)} className="w-8 h-8 flex items-center justify-center cursor-pointer whitespace-nowrap">
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        <ul className="px-4 md:px-16 pt-10 md:pt-16 flex flex-col gap-2 md:gap-3">
          {allAnchors.map((l, i) => (
            <li key={l.href} className="border-b border-background-50/10">
              <SmartLink href={l.href} onClick={() => setOpen(false)} className="flex items-baseline gap-6 py-3 md:py-4 font-heading font-light uppercase text-3xl md:text-5xl tracking-wide hover:text-accent-400 transition-colors cursor-pointer">
                <span className="label-xs text-background-50/40">{String(i + 1).padStart(2, '0')}</span>
                {l.label}
              </SmartLink>
            </li>
          ))}
        </ul>
      </div>
    </header>
  );
}